import { toLocalDateString } from '@/lib'
import type { FormState, TimeWindow } from './types'

export const SLOT_DURATION_MINUTES = 25
export const HOUR_HEIGHT_PX = 48
export const CALENDAR_START_HOUR = 7
export const CALENDAR_END_HOUR = 20
export const CALENDAR_TOTAL_HOURS = CALENDAR_END_HOUR - CALENDAR_START_HOUR
export const CALENDAR_HEIGHT_PX = CALENDAR_TOTAL_HOURS * HOUR_HEIGHT_PX

export const CALENDAR_HOUR_LABELS = Array.from(
	{ length: CALENDAR_TOTAL_HOURS + 1 },
	(_, i) => CALENDAR_START_HOUR + i,
).filter((h) => h % 2 !== 0)

export function formatHourLabel(hour: number): string {
	if (hour < 12) {
		return `${hour}a`
	}
	if (hour === 12) {
		return '12p'
	}
	return `${hour - 12}p`
}

export function timeToMinutes(time: string): number {
	const [h, m] = time.split(':').map(Number)
	return h * 60 + m
}

export function minutesToPx(totalMinutes: number): number {
	return ((totalMinutes - CALENDAR_START_HOUR * 60) / 60) * HOUR_HEIGHT_PX
}

export function durationToPx(durationMinutes: number): number {
	return (durationMinutes / 60) * HOUR_HEIGHT_PX
}

export function getCurrentWeekDates(): Date[] {
	const today = new Date()
	const dayOfWeek = today.getDay()
	const diffToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek
	const monday = new Date(today)
	monday.setDate(today.getDate() + diffToMonday)
	return Array.from({ length: 7 }, (_, i) => {
		const date = new Date(monday)
		date.setDate(monday.getDate() + i)
		return date
	})
}

export function dateToIsoDay(date: Date): number {
	const d = date.getDay()
	return d === 0 ? 7 : d
}

export function getEffectiveWindows(
	date: Date,
	form: FormState,
): { windows: TimeWindow[]; fullyBlocked: boolean; blockedWindows: TimeWindow[] } {
	const dateStr = toLocalDateString(date)
	const isoDay = dateToIsoDay(date)

	const activeOverrides = form.schedule.overrides.filter(
		(override) => dateStr >= override.from && dateStr <= override.to,
	)

	const fullDayBlock = activeOverrides.find((override) => !override.open && !override.start && !override.end)
	if (fullDayBlock) {
		return {
			windows: [],
			fullyBlocked: true,
			blockedWindows: [],
		}
	}

	const openOverrides = activeOverrides.filter((override) => override.open && override.start && override.end)
	if (openOverrides.length > 0) {
		return {
			windows: openOverrides.map((override) => ({
				start: timeToMinutes(override.start!),
				end: timeToMinutes(override.end!),
			})),
			fullyBlocked: false,
			blockedWindows: [],
		}
	}

	const windows = form.schedule.slots
		.filter((slot) => slot.day === isoDay)
		.map((slot) => ({
			start: timeToMinutes(slot.start),
			end: timeToMinutes(slot.end),
		}))

	const blockedWindows = activeOverrides
		.filter((override) => !override.open && override.start && override.end)
		.map((override) => ({
			start: timeToMinutes(override.start!),
			end: timeToMinutes(override.end!),
		}))

	return {
		windows,
		fullyBlocked: false,
		blockedWindows,
	}
}

/**
 * Converts a UTC ISO string to { dateStr, startMinutes, endMinutes } in the given IANA timezone.
 * Used to position confirmed bookings on the calendar preview.
 */
export function bookingToLocalBlock(
	scheduledAt: string,
	durationMinutes: number,
	timezone: string,
): { dateStr: string; startMinutes: number; endMinutes: number } {
	const date = new Date(scheduledAt)
	const fmt = new Intl.DateTimeFormat('en-CA', {
		timeZone: timezone,
		year: 'numeric',
		month: '2-digit',
		day: '2-digit',
		hour: '2-digit',
		minute: '2-digit',
		hour12: false,
	})
	const parts = Object.fromEntries(fmt.formatToParts(date).map((p) => [p.type, p.value]))
	const dateStr = `${parts.year}-${parts.month}-${parts.day}`
	const startMinutes = parseInt(parts.hour) * 60 + parseInt(parts.minute)
	return { dateStr, startMinutes, endMinutes: startMinutes + durationMinutes }
}

export function calculateSlots(
	windows: TimeWindow[],
	blockedWindows: TimeWindow[],
	bufferMinutes: number,
	maxPerDay: number,
	minStartMinutes = 0,
): { start: number; capped: boolean }[] {
	const slots: { start: number; capped: boolean }[] = []

	for (const window of windows) {
		let cursor = window.start
		while (cursor + SLOT_DURATION_MINUTES <= window.end) {
			const slotEnd = cursor + SLOT_DURATION_MINUTES
			const isBlocked = blockedWindows.some((bw) => cursor < bw.end && slotEnd > bw.start)
			if (!isBlocked && cursor >= minStartMinutes) {
				slots.push({
					start: cursor,
					capped: slots.length >= maxPerDay,
				})
			}
			cursor += SLOT_DURATION_MINUTES + bufferMinutes
		}
	}

	return slots
}
